import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Content,
  Page,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import {
  configApiRef,
  errorApiRef,
  fetchApiRef,
  identityApiRef,
  SessionState,
  discoveryApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { zitadelAuthApiRef } from '../../auth';
import { Box, Button, Typography } from '@material-ui/core';
import { parseEntityRef } from '@backstage/catalog-model';

type AccessDecision = {
  allowed: boolean;
  reason?: string;
};

const ACCESS_KEYS = ['allowed', 'isAllowed', 'hasAccess', 'access', 'enabled'];
const MESSAGE_KEYS = ['message', 'reason', 'detail', 'error'];

const parseAccessResponse = async (response: Response): Promise<AccessDecision> => {
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    try {
      const data = await response.clone().json();
      if (typeof data === 'boolean') {
        return { allowed: data };
      }

      if (typeof data === 'object' && data !== null) {
        for (const key of ACCESS_KEYS) {
          const value = (data as Record<string, unknown>)[key];
          if (typeof value === 'boolean') {
            const reasonKey = MESSAGE_KEYS.find(messageKey => typeof (data as Record<string, unknown>)[messageKey] === 'string');
            return {
              allowed: value,
              reason: reasonKey ? String((data as Record<string, unknown>)[reasonKey]) : undefined,
            };
          }
        }
      }
    } catch (error) {
      // Fall back to parsing as text if JSON parsing fails
      console.warn('Failed to parse Zitadel access response as JSON', error);
    }
  }

  try {
    const rawText = (await response.text()).trim();
    if (!rawText) {
      return { allowed: true };
    }
    const textBody = rawText.toLowerCase();
    if (textBody === 'false' || textBody === '0' || textBody === 'no') {
      return { allowed: false, reason: rawText };
    }
    if (textBody === 'true' || textBody === '1' || textBody === 'yes') {
      return { allowed: true };
    }
    // Treat any other non-empty textual response as a reason that implies denial.
    return { allowed: false, reason: rawText };
  } catch (error) {
    console.warn('Failed to parse Zitadel access response body', error);
  }

  return { allowed: true };
};

const templateEndpoint = (
  template: string,
  replacements: Record<string, string | undefined>,
) => {
  return template.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, key: string) => {
    const normalizedKey = key.toLowerCase();
    const value = replacements[key] ?? replacements[normalizedKey] ?? '';
    return encodeURIComponent(value ?? '');
  });
};

type GuardState = 'idle' | 'checking' | 'allowed' | 'blocked';

export const ZitadelAccessInterceptor = ({ children }: PropsWithChildren<{}>) => {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const errorApi = useApi(errorApiRef);
  const zitadelAuthApi = useApi(zitadelAuthApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const guardConfig = useMemo(() => {
    const enabled = configApi.getOptionalBoolean('app.zitadelAccess.enabled') ?? true;
    const endpointRaw = configApi.getOptionalString('app.zitadelAccess.guardEndpoint');
    const endpoint =
      endpointRaw && endpointRaw.trim().length > 0 ? endpointRaw.trim() : undefined;

    return { enabled, endpoint };
  }, [configApi]);

  const configuredBlockMessage =
    configApi.getOptionalString('app.zitadelAccess.blockMessage');

  const defaultBlockMessage =
    'Your access request is pending review. Please contact your administrator.';

  const [guardState, setGuardState] = useState<GuardState>(
    guardConfig.enabled ? 'checking' : 'allowed',
  );
  const [blockReason, setBlockReason] = useState<string | undefined>();

  const checkAccess = useCallback(async (): Promise<AccessDecision> => {
    if (!guardConfig.enabled) {
      return { allowed: true };
    }

    try {
      const [profile, identity, credentials] = await Promise.all([
        identityApi.getProfileInfo(),
        identityApi.getBackstageIdentity(),
        identityApi.getCredentials(),
      ]);

      const headers: Record<string, string> = {};
      if (credentials.token) {
        headers.Authorization = `Bearer ${credentials.token}`;
      }
      if (profile.email) {
        headers['X-User-Email'] = profile.email;
      }

      if (guardConfig.endpoint) {
        console.debug('Zitadel guard invoking custom endpoint', guardConfig.endpoint);
        let replacements: Record<string, string | undefined> = {
          email: profile.email ?? undefined,
          displayName: profile.displayName ?? undefined,
          picture: profile.picture ?? undefined,
          userentityref: identity.userEntityRef,
        };

        try {
          const entityRef = parseEntityRef(identity.userEntityRef);
          replacements = {
            ...replacements,
            userkind: entityRef.kind,
            usernamespace: entityRef.namespace,
            username: entityRef.name,
          };
        } catch (parseError) {
          console.warn('Failed to parse user entity ref for Zitadel guard', parseError);
        }

        const resolvedEndpoint = templateEndpoint(guardConfig.endpoint, replacements);
        const response = await fetchApi.fetch(resolvedEndpoint, {
          headers,
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(
            `Access guard responded with status ${response.status} ${response.statusText}`,
          );
        }

        return await parseAccessResponse(response);
      }

      const billingPortalBaseUrl = await discoveryApi.getBaseUrl('billing-portal');
      console.debug('Zitadel guard invoking billing portal guard', billingPortalBaseUrl);
      const response = await fetchApi.fetch(`${billingPortalBaseUrl}/access/guard`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: '{}',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(
          `Access guard responded with status ${response.status} ${response.statusText}`,
        );
      }

      return await parseAccessResponse(response);
    } catch (error) {
      errorApi.post(
        new Error(
          `Failed to verify Zitadel access guard: ${
            error instanceof Error ? error.message : String(error)
          }`,
        ),
      );
      console.error('Zitadel guard check failed', error);

      return { allowed: true };
    }
  }, [discoveryApi, errorApi, fetchApi, guardConfig, identityApi]);

  useEffect(() => {
    if (!guardConfig.enabled) {
      setGuardState('allowed');
      setBlockReason(undefined);
      return;
    }

    let isMounted = true;
    setGuardState('checking');

    const subscription = zitadelAuthApi.sessionState$().subscribe({
      next: sessionState => {
        if (!isMounted) {
          return;
        }

        if (sessionState === SessionState.SignedIn) {
          setGuardState('checking');
          checkAccess().then(decision => {
            if (!isMounted) {
              return;
            }

            console.info('Zitadel guard decision', decision);
            if (decision.allowed) {
              setGuardState('allowed');
              setBlockReason(undefined);
            } else {
              setGuardState('blocked');
              setBlockReason(decision.reason);
            }
          });
          return;
        }

        setGuardState('allowed');
        setBlockReason(undefined);
      },
      error: error => {
        if (!isMounted) {
          return;
        }
        errorApi.post(
          new Error(
            `Failed to observe Zitadel session state: ${
              error instanceof Error ? error.message : String(error)
            }`,
          ),
        );
        setGuardState('allowed');
        setBlockReason(undefined);
      },
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [checkAccess, errorApi, guardConfig.enabled, zitadelAuthApi]);

  if (!guardConfig.enabled) {
    return <>{children}</>;
  }

  if (guardState === 'checking' || guardState === 'idle') {
    return (
      <Page themeId="tool">
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  if (guardState === 'blocked') {
    const message = blockReason ?? configuredBlockMessage ?? defaultBlockMessage;
    const handleSignOut = () => {
      identityApi.signOut();
    };

    return (
      <Page themeId="tool">
        <Content>
          <WarningPanel
            severity="error"
            title="Access temporarily restricted"
          >
            <Typography variant="body1">{message}</Typography>
            <Box marginTop={2}>
              <Button variant="contained" color="primary" onClick={handleSignOut}>
                Sign out
              </Button>
            </Box>
          </WarningPanel>
        </Content>
      </Page>
    );
  }

  return <>{children}</>;
};
